/**
 * Data access for the patients module. Patient records are practice-wide
 * (ADR-0012), so reads go through the practice client; query keys start with
 * the `patient` entity key so the shell's live stream refetches them when any
 * workstation (or the PMS sync) changes the record.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PatientOverviewSchema,
  PatientSearchResponseSchema,
  PatientTimelineSchema,
  type PatientOverview,
  type PatientSearchHit,
  type PatientSearchQueryInput,
  type PatientTimeline,
  type TimelineFilter,
} from '@velora/contracts/patients';
import { ApiError, buildPath } from '@velora/sdk';
import { api, clientFor, currentOfficeClient } from '../../app/shell.js';

export const PATIENT_KEY = 'patient';

export const overviewKey = (id: string) => [PATIENT_KEY, id, 'overview'] as const;
export const timelineKey = (id: string, group: TimelineFilter, office: string | null) =>
  [PATIENT_KEY, id, 'timeline', group, office ?? 'all'] as const;

/** The record's query (shared by `usePatientOverview` and the open-time prefetch). */
export function overviewQuery(id: string) {
  return {
    queryKey: overviewKey(id),
    // Parsed with the patients contract itself (not the generated client), so
    // the not-synced availability (V18) can never be stripped by a stale SDK.
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.request(buildPath('/patients/:id', { id }), {
        schema: PatientOverviewSchema,
        signal,
      }) as Promise<PatientOverview>,
    staleTime: 15_000,
  };
}

export function usePatientOverview(id: string | null) {
  return useQuery({
    ...overviewQuery(id ?? 'none'),
    enabled: id !== null,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  });
}

export function usePatientTimeline(id: string, group: TimelineFilter, office: string | null) {
  return useQuery({
    queryKey: timelineKey(id, group, office),
    queryFn: ({ signal }) =>
      api.request(
        buildPath(
          '/patients/:id/timeline',
          { id },
          { group, limit: 200, ...(office ? { locationId: office } : {}) },
        ),
        { schema: PatientTimelineSchema, signal },
      ) as Promise<PatientTimeline>,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Patient search. `office` is the office selector value (`all` or an
 * office id) the API scopes the hits to; omitted, the office selected in the
 * address bar right now.
 */
export async function searchPatients(
  body: PatientSearchQueryInput,
  signal?: AbortSignal,
  office?: string,
): Promise<PatientSearchHit[]> {
  return (await searchPatientsWithStatus(body, signal, office)).hits;
}

/** Hits plus how many patients the index has not caught up on yet. */
export async function searchPatientsWithStatus(
  body: PatientSearchQueryInput,
  signal?: AbortSignal,
  office?: string,
): Promise<{ hits: PatientSearchHit[]; pending: number }> {
  const client = office ? clientFor(office) : currentOfficeClient();
  const res = await client.request('/patients/search', {
    method: 'POST',
    body,
    schema: PatientSearchResponseSchema,
    ...(signal ? { signal } : {}),
  });
  return { hits: res.hits as PatientSearchHit[], pending: res.pending ?? 0 };
}

/** One key per user intent: a retried click replays, never duplicates. */
export function intentKey(prefix: string): string {
  const rnd =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${rnd}`.slice(0, 120);
}

/** Readable message for an API failure (the envelope's message, never PHI). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: unknown } } | null;
    const msg = body?.error?.message;
    return typeof msg === 'string' ? msg : `The server answered ${err.status}.`;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}

/** Invalidates everything cached for these patients (overview, timeline, peek). */
export function useInvalidatePatients() {
  const qc = useQueryClient();
  return (ids: readonly (string | null | undefined)[]) =>
    Promise.all(
      ids
        .filter((x): x is string => typeof x === 'string')
        .map((id) => qc.invalidateQueries({ queryKey: [PATIENT_KEY, id] })),
    );
}

/** A mutation that refreshes the given patients when it settles. */
export function usePatientMutation<TVars, TResult>(
  fn: (vars: TVars) => Promise<TResult>,
  affected: (vars: TVars, result: TResult | undefined) => (string | null | undefined)[],
) {
  const invalidate = useInvalidatePatients();
  return useMutation({
    mutationFn: fn,
    onSettled: (result, _err, vars) => invalidate(affected(vars, result)),
  });
}
